import Vue from "vue";
import { getEvmChainMeta } from "../config/evm-chains";
import { getConfig } from "../recovered/trade-api";

// 顶部的「链/计价币」选择器状态：ETH/USDC、ETH/USDT、BNB/USDC、BNB/USDT …
// 链和币种来自 config 接口的 chains 字段；授权用的合约与 spender 也从这里取。
const QUOTE_KEY = "U_QUOTE";
const CHAIN_KEY = "U_CHAIN_CODE";

// 暂不开放的链：接口里返回了也不进状态。
// 在入口处过滤而不是只在下拉里隐藏，避免存量 U_CHAIN_CODE 指向被隐藏的链、
// 出现「下拉看不到但授权仍走该链」的情况。要放开就从这里删掉对应 code。
const HIDDEN_CHAIN_CODES = ["bsc"];

// config 还没回来时的兜底：只保证顶部有东西显示。contract/spender 留空，
// 授权时会因缺配置直接报错，不会拿着猜来的合约地址去 approve。
const FALLBACK_CHAINS = [
  {
    code: "erc",
    name: "Ethereum",
    protocol: "evm",
    nativeSymbol: "ETH",
    spender: "",
    tokens: [
      { symbol: "USDC", contract: "", decimals: 6, isNative: false },
      { symbol: "USDT", contract: "", decimals: 6, isNative: false },
    ],
  },
];

function normalizeQuote(raw) {
  return String(raw || "").toUpperCase() === "USDT" ? "USDT" : "USDC";
}

export const quoteState = Vue.observable({
  chains: FALLBACK_CHAINS.slice(),
  chainCode: String(localStorage.getItem(CHAIN_KEY) || "erc").toLowerCase(),
  coin: normalizeQuote(localStorage.getItem(QUOTE_KEY) || "USDC"),
});

export function getChains() {
  return quoteState.chains;
}

export function getQuote() {
  return quoteState.coin;
}

export function getChainCode() {
  return quoteState.chainCode;
}

export function findChain(code) {
  const want = String(code || "").toLowerCase();
  return quoteState.chains.find((c) => c.code === want) || quoteState.chains[0] || null;
}

export function getCurrentChain() {
  return findChain(quoteState.chainCode);
}

// 顶部可选的「链 + 计价币」组合，只列非原生代币（USDT / USDC 这类）
export function getPairs() {
  const pairs = [];
  quoteState.chains.forEach((chain) => {
    const native = chain.nativeSymbol || chain.code.toUpperCase();
    chain.tokens
      .filter((t) => !t.isNative)
      .forEach((token) => {
        pairs.push({
          key: `${chain.code}:${token.symbol}`,
          chainCode: chain.code,
          chainName: chain.name,
          nativeSymbol: native,
          coin: token.symbol,
          label: `${native}/${token.symbol}`,
        });
      });
  });
  return pairs;
}

export function setQuote(coin) {
  const n = normalizeQuote(coin);
  quoteState.coin = n;
  localStorage.setItem(QUOTE_KEY, n);
}

export function setChainCode(code) {
  const chain = findChain(code);
  if (!chain) return;
  quoteState.chainCode = chain.code;
  localStorage.setItem(CHAIN_KEY, chain.code);
}

export function setPair(chainCode, coin) {
  setChainCode(chainCode);
  setQuote(coin);
}

// 用 config.chains 替换链表，并把当前选择校正到新链表里存在的组合
export function setChains(list) {
  const chains = (Array.isArray(list) ? list : []).filter(
    (c) =>
      c &&
      c.code &&
      HIDDEN_CHAIN_CODES.indexOf(c.code) < 0 &&
      Array.isArray(c.tokens) &&
      c.tokens.length
  );
  if (!chains.length) return false;

  quoteState.chains = chains;

  const chain = findChain(quoteState.chainCode) || chains[0];
  quoteState.chainCode = chain.code;
  localStorage.setItem(CHAIN_KEY, chain.code);

  // 选中的币在该链上不存在时，退到该链第一个非原生代币
  const hasCoin = chain.tokens.some((t) => !t.isNative && t.symbol === quoteState.coin);
  if (!hasCoin) {
    const first = chain.tokens.find((t) => !t.isNative);
    if (first) setQuote(first.symbol);
  }

  return true;
}

// config 的单次共享拉取：顶部选择器在所有页面都要链表，首页 bootstrap 也要整份 config，
// 共用同一个 promise 避免重复请求；首页刷新数据时传 force 重新拉。
let configPromise = null;

export function loadChainConfig(force) {
  if (!force && configPromise) return configPromise;

  configPromise = getConfig()
    .then((cfg) => {
      if (cfg) setChains(cfg.chains);
      return cfg;
    })
    .catch((err) => {
      configPromise = null; // 失败不缓存，下次可重试
      throw err;
    });

  return configPromise;
}

// 当前选择对应的授权参数；缺任一项都返回 ok:false，由调用方给出明确报错
export function getApproveTarget() {
  const chain = getCurrentChain();
  if (!chain) return { ok: false, reason: "chain" };

  const token = chain.tokens.find((t) => t.symbol === quoteState.coin && !t.isNative);
  if (!token || !token.contract) {
    return { ok: false, reason: "token", chainCode: chain.code, symbol: quoteState.coin };
  }
  if (!chain.spender) {
    return { ok: false, reason: "spender", chainCode: chain.code, symbol: quoteState.coin };
  }

  return {
    ok: true,
    chainCode: chain.code,
    chainName: chain.name,
    protocol: chain.protocol,
    symbol: token.symbol,
    tokenContract: token.contract,
    decimals: token.decimals,
    spender: chain.spender,
    meta: getEvmChainMeta(chain.code),
  };
}

export default {
  install(VueCtor) {
    Object.defineProperty(VueCtor.prototype, "$quote", {
      get() {
        return quoteState.coin;
      },
    });
    Object.defineProperty(VueCtor.prototype, "$chainCode", {
      get() {
        return quoteState.chainCode;
      },
    });
    // 当前链的原生币符号（ETH / BNB），顶部标题用
    Object.defineProperty(VueCtor.prototype, "$chainSymbol", {
      get() {
        const chain = getCurrentChain();
        return (chain && chain.nativeSymbol) || "ETH";
      },
    });
    VueCtor.prototype.$setQuote = setQuote;
    VueCtor.prototype.$setPair = setPair;
  },
};
